import React, { useState } from 'react';
import {
  ToggleButtonGroup,
  ToggleButton,
  Box,
  Typography,
  Paper
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { colorPrimary } from '../../Styles/Colors';

const SegmentedButton = ({
  options = [],
  value = null,
  onChange = () => {},
  variant = "standard", // standard, outlined, filled
  size = "medium", // small, medium, large
  color = "primary", // primary, secondary, info, success, warning, error
  exclusive = true,
  disabled = false,
  orientation = "horizontal", // horizontal, vertical
  fullWidth = false,
  sx = {},
  ...props
}) => {
  const [internalValue, setInternalValue] = useState(value);
  
  const handleChange = (event, newValue) => {
    if (!exclusive && newValue === null) {
      return; // Prevent deselecting all in non-exclusive mode
    }
    
    setInternalValue(newValue);
    onChange(event, newValue);
  };

  const currentValue = value !== null ? value : internalValue;

  // Default options if none provided
  const defaultOptions = [
    { value: 'label1', label: 'Label' },
    { value: 'label2', label: 'Label' },
    { value: 'label3', label: 'Label' }
  ];

  const optionsToRender = options.length > 0 ? options : defaultOptions;

  const getButtonStyles = () => {
    const baseStyles = {
      textTransform: 'none',
      fontWeight: 400,
      fontSize: '14px',
      minHeight: '40px',
      border: `1px solid ${colorPrimary}`,
      borderRadius: '20px',
      backgroundColor: 'transparent',
      color: '#1D192B',
      padding: '8px 24px',
      '&.Mui-selected': {
        backgroundColor: colorPrimary,
        color: 'white',
        border: `1px solid ${colorPrimary}`,
        '&:hover': {
          backgroundColor: colorPrimary,
        }
      },
      '&:hover': {
        backgroundColor: 'rgba(142, 78, 198, 0.04)',
        borderColor: colorPrimary
      },
      '&:not(:first-of-type)': {
        marginLeft: '-1px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
      },
      '&:not(:last-of-type)': {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
      },
      '&:first-of-type': {
        borderTopLeftRadius: '20px',
        borderBottomLeftRadius: '20px',
      },
      '&:last-of-type': {
        borderTopRightRadius: '20px',
        borderBottomRightRadius: '20px',
      }
    };

    if (variant === 'outlined') {
      return {
        ...baseStyles,
        border: '2px solid #79747E',
        '&.Mui-selected': {
          ...baseStyles['&.Mui-selected'],
          border: `2px solid ${colorPrimary}`
        },
        '&:not(:first-of-type)': {
          ...baseStyles['&:not(:first-of-type)'],
          marginLeft: '-2px',
        }
      };
    }

    if (variant === 'filled') {
      return {
        ...baseStyles,
        backgroundColor: '#F3F0F8',
        border: '1px solid #E7DFEC',
        '&.Mui-selected': {
          ...baseStyles['&.Mui-selected'],
          backgroundColor: colorPrimary
        }
      };
    }

    return baseStyles;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: fullWidth ? '100%' : 'fit-content',
        ...sx
      }}
    >
      <ToggleButtonGroup
        value={currentValue}
        exclusive={exclusive}
        onChange={handleChange}
        orientation={orientation}
        size={size}
        disabled={disabled}
        sx={{
          '& .MuiToggleButtonGroup-grouped': {
            margin: 0,
            border: 0,
            '&:not(:first-of-type)': {
              marginLeft: 0
            }
          }
        }}
        {...props}
      >
        {optionsToRender.map((option) => {
          const isSelected = exclusive ? 
            currentValue === option.value : 
            (Array.isArray(currentValue) && currentValue.includes(option.value));
          
          return (
            <ToggleButton
              key={option.value}
              value={option.value}
              disabled={option.disabled || disabled}
              sx={getButtonStyles()}
            >
              {isSelected && (
                <CheckIcon sx={{ mr: 1, fontSize: '16px' }} />
              )}
              {option.icon && !isSelected && (
                <Box sx={{ mr: option.label ? 1 : 0, display: 'flex', alignItems: 'center' }}>
                  {option.icon}
                </Box>
              )}
              {option.label && (
                <Typography variant="body2" component="span" sx={{ fontSize: '14px' }}>
                  {option.label}
                </Typography>
              )}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
    </Box>
  );
};

export default SegmentedButton;
